import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { BadgePreview } from "../docs/components/DocPrimitives";
import "../docs/EntryBadge.css";
import "./HowToRunSweepstakes.css";

const pageSchema = {
  "@type": "HowTo",
  name: "How to run a sweepstakes on Shopify",
  description:
    "An informal, step by step guide to official rules, bonding, AMOE, storefront setup, and winner selection.",
  step: [
    {
      "@type": "HowToStep",
      name: "Write your official rules",
      text: "Clearly cover eligibility, prize information, dates, entry methods, and winner selection.",
    },
    {
      "@type": "HowToStep",
      name: "Set up an alternate method of entry",
      text: "Make your free alternate method of entry real, easy to find, and functional.",
    },
    {
      "@type": "HowToStep",
      name: "Handle bonding and state filings",
      text: "Determine whether your promotion requires bonding and registration in Florida or New York.",
    },
    {
      "@type": "HowToStep",
      name: "Set up your website and entry displays",
      text: "Configure entry promotions and show customers the entries they earn.",
    },
    {
      "@type": "HowToStep",
      name: "Select a winner and complete required reporting",
      text: "Select your winner, award the prize, and submit any required winner certifications.",
    },
  ],
};

const HowToRunSweepstakes = () => {
  return (
    <main className="sweepstakes-guide">
      <Seo
        title="How to Run a Sweepstakes | An Informal Guide"
        description="Brian's informal, step by step guide to sweepstakes rules, bonding in Florida and New York, AMOE, Shopify entry displays, and winner selection."
        path="/how-to-run-a-sweepstakes"
        image="/og/how-to-run-sweepstakes.png"
        imageAlt="How to run a sweepstakes"
        schema={pageSchema}
      />

      <header className="sweepstakes-guide__hero">
        <h1>How to run a sweepstakes (legally)</h1>
        <p className="sweepstakes-guide__lead">
          This is an informal guide. It might have a few typos, but it is
          written in my own words (no AI slop), and I hope it can help you.
        </p>
        <p className="sweepstakes-guide__byline">
          --Brian, Founder and CTO of Clean Sweeps
        </p>
      </header>

      <aside className="sweepstakes-guide__disclaimer">
        <strong>Important: this is not legal advice.</strong>
        <p>
          Everything in this guide is my personal experience and opinion.
          Sweepstakes laws and requirements can vary by promotion and
          location. Talk with a qualified attorney about your specific
          sweepstakes before you launch it.
        </p>
      </aside>

      <nav className="sweepstakes-guide__outline" aria-label="Guide sections">
        <strong>Sweepstakes checklist</strong>
        <ul>
          <li><a href="#administration-companies">Administration companies</a></li>
          <li className="sweepstakes-guide__required">
            <span aria-label="Required">★</span>
            <a href="#official-rules">Official rules</a>
          </li>
          <li className="sweepstakes-guide__required">
            <span aria-label="Required">★</span>
            <a href="#bonding">Bonding when required</a>
          </li>
          <li className="sweepstakes-guide__required">
            <span aria-label="Required">★</span>
            <a href="#amoe">AMOE</a>
          </li>
          <li><a href="#website-setup">Website and entry displays</a></li>
          <li><a href="#marketing">Marketing</a></li>
        </ul>
        <p>
          The starred are the minimal requirements to run a sweepstakes (legally). Exact requirements can vary. The other items are there to help
          make your sweepstakes more successful.
        </p>
      </nav>

      <article className="sweepstakes-guide__content">
        <section id="administration-companies">
          <h2>1. Sweepstakes administration companies</h2>
          <p>
            I&apos;m not going to name any companies here, but most sweepstakes
            administration companies are very expensive. Some charge as much
            as $8,000 (per sweepstakes) just for bonding and official rules.
          </p>
          <p>They usually provide:</p>
          <ul>
            <li>Official rules</li>
            <li>Bonding services</li>
            <li>AMOE handling</li>
            <li>Winner selection</li>
          </ul>

          <h3>Do I need one to write rules for me?</h3>
          <p>
            Probably not in most cases. What is the point of rules? Well-written
            official rules are there to <strong>protect you and your business</strong>.
            They should keep your sweepstakes transparent by covering details,
            dates, name, eligibility, and so on. They should also make it
            legally defensible and provide a clear AMOE (Alternate Method of
            Entry). That last part is what helps keep your sweepstakes from
            becoming an illegal lottery. We&apos;ll get to AMOE later.
          </p>

          <h3>What else do administration companies handle?</h3>
          <ul>
            <li>
              <strong>Bonding:</strong> If the total prize value is more than
              $5,000 and the promotion is open to Florida or New York, state
              registration and financial security may be required. You do not
              necessarily need an administration company to handle this. When I
              did it myself, the total was around $400.
            </li>
            <li>
              <strong>AMOE:</strong> They can set up inboxes for mail in AMOE.
              Clean Sweeps offers mail in validation and a secure online AMOE
              form, depending on what your rules and legal counsel allow.
            </li>
            <li>
              <strong>Winner selection:</strong> They can select the winner for
              you, but you can also use our free{" "}
              <Link to="/winner-select">winner select tool</Link>.
            </li>
          </ul>

          <p>
            So, do you need a sweepstakes administration company? No. Are they
            ripping you off? Probably. Well, sort of. If you don&apos;t want to be
            bothered with writing your own rules and handling bonding, they can
            do that work for you. They still will <strong>NOT</strong> assume
            liability if you mess up while running your sweepstakes.
          </p>
          <p>
            You are essentially paying someone a huge markup to copy and paste
            rules and deal with some paperwork for bonding.
          </p>
        </section>

        <section id="official-rules">
          <h2>2. Official rules</h2>
          <p>
            Why do you need official rules? Because they protect your business.
            Your rules should clearly state:
          </p>
          <ul>
            <li>Who is eligible</li>
            <li>Prize information</li>
            <li>Start dates, end dates, and deadlines</li>
            <li>Every entry method</li>
            <li>How the winner will be selected</li>
          </ul>
          <p>
            The point is to help customers understand the promotion and reduce
            the risk of disputes or compliance issues.
          </p>

          <div className="sweepstakes-guide__callout">
            <strong>Important note</strong>
            <p>
              For a sweepstakes not to be considered a lottery, there must be a
              real free way to enter. You will commonly see language like:
            </p>
            <blockquote>
              No purchase necessary to enter or win. A purchase will not
              increase your chances of winning.
            </blockquote>
          </div>

          <p>
            This means that when a purchase earns entries, customers must also
            be able to earn entries through the AMOE described in your rules. I
            won&apos;t go deep into the legal details here, but read the official
            rules of a major sweepstakes and look at the mail in entry section
            and instructions for claiming specific entry promotions. You&apos;ll see
            what I mean.
          </p>
          <p>
            <strong>
              Can you take official rules from another company and alter them to match the details for your sweepstakes?
            </strong>{" "}
            I&apos;m not sure really, but I think it&apos;s something I would do if I wanted to run my own sweepstakes. <em>I
            think I&apos;m supposed to tell you to speak with an attourney here. Once again, these are just my opinions, not legal advice.</em>
          </p>

          <h3>Abbreviated rules</h3>
          <p>
            These are the short rules you usually see in a website footer or
            advertisement. They often start with the no purchase necessary
            language above and point to the full official rules. Make sure the
            abbreviated rules for your promotion include all disclosures your
            attorney says are required.
          </p>
        </section>

        <section id="bonding">
          <h2>3. Bonding</h2>
          <p>
            This is a pain point for a lot of new sweepstakes companies. Most
            will pay an administration company to handle bonding. Is your total
            prize value more than $5,000? If yes, and your sweepstakes is open
            to Florida or New York residents, you will need to register and
            bond it in those states.
          </p>
          <p>
            Can you run a sweepstakes without bonding in Florida and New York if your prize is over $5000?
            One option may be to clearly exclude Florida and New York residents
            in both your official and abbreviated rules, but confirm that choice
            with your attorney (but then again i'm not here to give legal advice).
          </p>

          <div className="sweepstakes-guide__callout">
            <strong>Have your official rules ready before you file.</strong>
            <p>
              Your filing and bond are based on the promotion described in
              those rules. If something changes, check with the state and your
              attorney before editing the rules.
            </p>
          </div>

          <p>
            <strong>
              Now for the part you&apos;re probably here for: how to handle bonding
              in Florida and New York.
            </strong>{" "}
            Reach out to{" "}
            <a
              href="https://my.alphasurety.com/new-bond.aspx"
              target="_blank"
              rel="noopener noreferrer"
            >
              <strong>Alpha Surety</strong>
            </a>{" "}
            and fill out the online bond application. If you speak to Ashton
            or Jacquelyn, tell them Brian from Clean Sweeps sent you. Alpha
            Surety can provide the surety bonds and power of attorney documents
            needed for both states. Alpha Surety also has an overnight mail service, if you need the paperwork ASAP.
          
          </p>

          <h3>Florida</h3>
          <ol>
            <li>
              Take the Florida paperwork Alpha Surety sent you. It will contain
              sticky notes showing where you need to sign and where witness
              signatures are required. Make sure all required signatures are
              included.
            </li>
            <li>
              Create an account on the{" "}
              <a
                href="https://consumercompliance.fdacs.gov/"
                target="_blank"
                rel="noopener noreferrer"
              >
                FDACS Consumer Compliance portal
              </a>.
            </li>
            <li>
              Log in, select <em>Game Promotion Filing Application</em>, and
              enter the prize, dates, and other promotion details.
            </li>
            <li>
              Scan and upload the requested documents, including your official
              rules, signed surety bond, and power of attorney.
            </li>
            <li>Pay the $100 filing fee and submit.</li>
            <li>
              Watch for a confirmation email or a request for more information. Florida will usually get back to you within a few days. They will respond via email whether they need more info or if you're approved.
            </li>
          </ol>
          <p>
            Florida requires promotions with more than $5,000 in prizes to file
            at least seven days before the start date. After the winners are
            finally determined, you have 60 days to send the required certified
            winners list. <strong> IMPORTANT: DO NOT FORGET THIS!</strong> They will come after you and fine
            you lol. I have a client that got fined by Florida because their winner list was 1 day late (really).
          </p>
          <p className="sweepstakes-guide__source">
            Double-check the current requirements on the{" "}
            <a
              href="https://www.fdacs.gov/Business-Services/Game-Promotions-Sweepstakes"
              target="_blank"
              rel="noopener noreferrer"
            >
              official Florida game promotions page
            </a>.
          </p>

          <h3>New York</h3>
          <p>
            New York is annoying because there is no online filing portal,
            so you need to use snail mail.
          </p>
          <ol>
            <li>
              Complete and sign the bond that your surety company sends you.
              {" "}<strong>This paperwork NEEDS TO BE NOTARIZED.</strong> New York
              will kick back your application if you are missing signatures or
              notarization.
            </li>
            <li>
              Fill out the official{" "}
              <a
                href="https://dos.ny.gov/system/files/documents/2018/09/0255-f.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Games of Chance Registration
              </a>.
            </li>
            <li>
              Write a $100 check payable to <em>NYS Department of State</em>.
            </li>
            <li>
              Put your registration, official rules, surety bond, and check in
              one envelope.
            </li>
            <li>
              Mail it to the longest address in the universe:
              <address>
                New York Department of State<br />
                Division of Corporations, State Records and Uniform Commercial
                Code<br />
                State Records Bureau<br />
                One Commerce Plaza<br />
                99 Washington Avenue<br />
                Albany, NY 12231
              </address>
              <p className="sweepstakes-guide__source">
                *** This address really grinds my gears, because it&apos;s so
                long that if you wanted to print a shipping label, there is no
                template on USPS, UPS or FEDEX available that can handle an
                address this long. So you are are forced to snail mail it. If
                you find a way around this, please let me know so I can update
                this guide
              </p>
            </li>
            <li>
              New York will send a filing receipt or contact you if something
              needs to be corrected.
            </li>
          </ol>
          <p>
            New York requires a Certification of Winners within 90 days after
            the promotion ends. It must list each winner of a prize worth more
            than $25 and the required prize and delivery details. I'm not saying this, but allegedly, NY is FAR less strict than FL about the winners list and doesn't really seemt to care if you send it or not (allegedly). Send the list.
          </p>
          <p className="sweepstakes-guide__source">
            Double-check the current forms and requirements on the{" "}
            <a
              href="https://dos.ny.gov/games-chance-registration"
              target="_blank"
              rel="noopener noreferrer"
            >
              official New York Games of Chance page
            </a>.
          </p>
          <p>
            Once you have received confirmation from both states, you are
            bonded for your sweepstakes! <strong>DO NOT ALTER YOUR RULES WITHOUT
            CONSULTING AN ATTOURNEY.</strong> Your official rules are now bonded.
          </p>
        </section>

        <section id="amoe">
          <h2>4. AMOE</h2>
          <p>
            AMOE is the “no purchase necessary” piece of your rules. Most
            companies use snail mail. You can require customers to fill out an
            envelope and mail it to you, then provide entries based on that
            mail-in and your official rules.
          </p>
          <p>Your AMOE must be:</p>
          <ul>
            <li>Real</li>
            <li>Easy to find</li>
            <li>Fair</li>
            <li>Actually working</li>
          </ul>
          <p>
            Regulators may test it. If the AMOE is broken, hidden, or unfair,
            the promotion could be challenged as an illegal lottery.
          </p>

          <h3>Entry promotions and your AMOE</h3>
          <p>
            In order to make the statement <strong>“No purchase necessary to
            enter or win. A purchase will not increase your chances of
            winning.”</strong> valid, your AMOE must provide the same entry
            promotions that are available on your website, such as a 10x entry
            promotion.
          </p>
          <p>
            You can do this by allowing entrants to handwrite promotions onto
            the AMOE card or by using the promotion fields in the{" "}
            <Link to="/what-we-do"><strong>Clean Sweeps AMOE form</strong></Link>.
          </p>
          <p>
            See major sweepstakes company rules for examples of how this is
            written into the official rules.
          </p>

          <p>
            Lucky for you, we have an answer. Clean Sweeps provides an online
            form if you don&apos;t want to deal only with mail ins, plus an online
            form with mail in validation. Read about the AMOE tools on our{" "}
            <Link to="/what-we-do">features page</Link> to see how we handle
            this headache for you, then confirm the method in your official
            rules with counsel.
          </p>
        </section>

        <section id="website-setup">
          <h2>5. Setting up your website and entry displays</h2>
          <p>
            Clean Sweeps handles everything for you regarding sweepstakes
            entries: bonus spending entries, entry promotions, discount code
            entries, and affiliate link entries. Whatever you can think of, we
            can probably do it.
          </p>
          <ol>
            <li>Install the Clean Sweeps app.</li>
            <li>Follow the <Link to="/setup">setup guides</Link>.</li>
            <li>Turn on the widgets that show customers their entries.</li>
          </ol>
          <h3>Custom displays connected to our API</h3>
          <p>
            For custom displays connected to our API, see the{" "}
            <Link to="/api-documentation">API documentation</Link> and the{" "}
            <Link to="/api-documentation/guides/entry-displays">
              entry display guide
            </Link>.
          </p>
          <div className="sweepstakes-guide__api-preview">
            <BadgePreview />
            <p className="sweepstakes-guide__source">Example display</p>
          </div>
          <p>
            If you see a cool custom display and want us to build it for you,
            reach out for pricing on custom dev work.
          </p>
        </section>

        <section id="marketing">
          <h2>6. Marketing (this is the entire business)</h2>
          <p>
            I could write an entire book on this piece. If you&apos;re a Diamond
            subscriber and want some free marketing consulting, please{" "}
            <Link to="/consulting">reach out</Link> and ask for Brian or Kent.
          </p>
          <p>
            This is actually free. We want your sweepstakes to do well. If it
            does well, you will stay subscribed to Clean Sweeps. That is a win
            for everyone.
          </p>
        </section>

        <section className="sweepstakes-guide__closing">
          <h2>You&apos;re ready to go</h2>
          <p>You&apos;re ready to go and start your own sweepstakes.</p>
          <p>
            If you need to contact us, please email{" "}
            <a href="mailto:info@cleansweeps.app">info@cleansweeps.app</a>.
          </p>
          <p>Your sweepstakes being a success is a success for us as well!</p>
        </section>
      </article>
    </main>
  );
};

export default HowToRunSweepstakes;
